import { Box, Button, ButtonDropdown, Header, Link, Pagination, SpaceBetween, Table, TextFilter, CollectionPreferences } from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Asset } from "../../types";
import { useAuth } from "react-oidc-context";
import { getEndpoint } from "../../utils";

export default function AdminAssetsTable() {
    const navigate = useNavigate();
    const auth = useAuth();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageIndex, setPageIndex] = useState(1);
    const [multiSelectActionsDisabled, setMultiSelectActionsDisabled] = useState(true);
    const [filteringText, setFilteringText] = useState("");
    const [preferences, setPreferences] = useState({ pageSize: 10 });

   
    const handleDeleteAssets = async () => {
        try {
            const results = await Promise.all(selectedAssets.map(async (asset) => {
                const response = await fetch(getEndpoint(`/api/admin/assets/${encodeURIComponent(asset.path)}`), {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${auth.user?.id_token}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Failed to delete asset ${asset.path}: ${response.statusText}`);
                }
                return asset.path;
            }));
            
            console.log(`Successfully deleted assets: ${results.join(', ')}`);
            setAssetsLoaded(false);
            setSelectedAssets([]);
        } catch (error) {
            console.error('Failed to delete assets:', error);
            // You might want to add a UI notification here
        }
    };

    useEffect(() => {
        if (!assetsLoaded) {
            try {
                console.log('Loading assets');
                setLoading(true);
                if (!auth.user?.id_token || auth.user?.id_token === '') {
                    throw new Error('No access token available');
                }
                // This is a workaround for local development where proxy is dropping the /api prefix
                const endpoint = getEndpoint('/api/admin/assets')

                const token = auth.user.id_token
                if (!token) {
                    return;
                }
                fetch(endpoint, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }).then((response) => {
                    if (!response.ok) {
                        response.text().then((t) => console.error(t));
                        return;
                    } else {
                        response.json().then((data) => {
                            setAssets(data);
                            setAssetsLoaded(true);
                            setLoading(false);
                        })

                    }

                })

            } catch (e) {
                console.error(e);
                setLoading(false);
            }
        }
    }, [assets, assetsLoaded, auth]);

    const filteredAssets = assets.filter(asset =>
        asset.path?.toLowerCase().includes(filteringText.toLowerCase()) ||
        asset.note?.toLowerCase().includes(filteringText.toLowerCase())
    );
    const startIndex = (pageIndex - 1) * preferences.pageSize;
    const endIndex = startIndex + preferences.pageSize;
    const currentPageAssets = filteredAssets.slice(startIndex, endIndex);

    return (
        <Table
            key="Admin-Assets-Table"
            renderAriaLive={({
                firstIndex,
                lastIndex,
                totalItemsCount
            }) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={({ detail }) => {
                setSelectedAssets(detail.selectedItems)
                setMultiSelectActionsDisabled(detail.selectedItems.length < 1)
            }}
            selectedItems={selectedAssets}
            ariaLabels={{
                selectionGroupLabel: "Item selection",
                allItemsSelectionLabel: () => "Select all items",
            }}
            columnDefinitions={[
                {
                    id: "path",
                    header: "Path",
                    cell: (item) => <Link href={`/admin/asset/${encodeURIComponent(item.path)}`}>{item.path}</Link>,
                    sortingField: "path",
                    isRowHeader: true,
                },
                {
                    id: "note",
                    header: "Note",
                    cell: (item) => item.note,
                    sortingField: "note",
                },
                {
                    id: "fileType",
                    header: "Type",
                    cell: (item) => item.fileType,
                    sortingField: "fileType",
                }
            ]}
            resizableColumns
            items={currentPageAssets}
            loadingText="Loading items..."
            loading={loading}
            trackBy="path"
            selectionType="multi"
            empty={
                <Box
                    key="assets-empty-box"
                    margin={{ top: "l" }}
                    textAlign="center"
                    color="inherit"
                >
                    <SpaceBetween key="assets-empty-spacer" size="m">
                        <b key="assets-empty-b-text">It's quiet... too quiet. No assets were loaded...</b>
                    </SpaceBetween>
                </Box>
            }
            filter={
                <TextFilter
                    key={"assets-text-filter"}
                    filteringPlaceholder="Find resources"
                    filteringText={filteringText}
                    onChange={({ detail }) => {
                        setFilteringText(detail.filteringText);
                        setPageIndex(1);
                    }}
                />
            }
            header={
                <Header
                    key="assets-header"
                    counter={
                        selectedAssets.length ? "(" + selectedAssets.length + "/" + assets.length + ")" : "(" + assets.length + ")"
                    }
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <ButtonDropdown
                                items={[
                                    {
                                        text: "Delete",
                                        id: "delete",
                                        disabled: multiSelectActionsDisabled
                                    }
                                ]}
                                onItemClick={({ detail }) => {
                                    if (detail.id === 'delete') {
                                        handleDeleteAssets();
                                    }
                                }}>Actions</ButtonDropdown>
                            <Button variant="primary" onClick={() => navigate('/admin/asset')}>Add Asset</Button>
                        </SpaceBetween>

                    }
                >
                    Configured Assets
                </Header>
            }
            preferences={
                <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    pageSizePreference={{
                        title: "Select page size",
                        options: [
                            { value: 5, label: "5 resources" },
                            { value: 10, label: "10 resources" },
                            { value: 25, label: "25 resources" }
                        ]
                    }}
                    preferences={{ pageSize: preferences.pageSize }}
                    onConfirm={({ detail }) => {
                        setPreferences({ pageSize: detail.pageSize ?? 10 });
                        setPageIndex(1);
                    }}
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={pageIndex}
                    pagesCount={Math.ceil(filteredAssets.length / preferences.pageSize)}
                    onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
                />
            }
        />
    )
}
